import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  DollarSign, Users, Mail, Building2, Newspaper, Trophy,
  LogOut, RefreshCw, Check, X, Eye, Edit, Trash2, Plus,
  Download, ChevronDown, ChevronUp, Loader2, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'lane4_admin_token';

// Selectable times in 30-minute steps: "12:00 AM" through "11:30 PM".
const TIME_OPTIONS = (() => {
  const options = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += 30) {
    const hour24 = Math.floor(minutes / 60);
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    const mins = String(minutes % 60).padStart(2, '0');
    options.push(`${hour12}:${mins} ${hour24 < 12 ? 'AM' : 'PM'}`);
  }
  return options;
})();

// Events store time as a single string ("9:00 AM - 12:00 PM"); split it for editing.
const parseTimeRange = (value) => {
  if (!value) return { start: '', end: '' };
  const [start = '', end = ''] = value.split(/\s+-\s+/);
  return { start: start.trim(), end: end.trim() };
};

// Keep a previously saved time selectable even if it isn't on the 30-minute grid,
// so editing an older event can't silently discard it.
const timeChoices = (current) =>
  current && !TIME_OPTIONS.includes(current) ? [current, ...TIME_OPTIONS] : TIME_OPTIONS;

// The admin session token is held here and attached to every API request.
// Kept at module scope so the interceptors below are registered exactly once.
let adminToken = (() => {
  try {
    return localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
})();

const setAdminToken = (token) => {
  adminToken = token || '';
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    /* storage unavailable (private mode) - token still works for this session */
  }
};

// Set by the component so an expired token can bounce the user back to the login screen.
let handleUnauthorized = () => {};

axios.interceptors.request.use((config) => {
  if (adminToken && config.url && config.url.startsWith(API)) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${adminToken}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      setAdminToken('');
      handleUnauthorized();
    }
    return Promise.reject(error);
  }
);

const Admin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  
  const [dashboardStats, setDashboardStats] = useState(null);
  const [donations, setDonations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [athletes, setAthletes] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Athlete modal state
  const [isAthleteModalOpen, setIsAthleteModalOpen] = useState(false);
  const [editingAthlete, setEditingAthlete] = useState(null);
  const [athleteForm, setAthleteForm] = useState({
    name: '',
    age: '',
    sport: '',
    achievement: '',
    story: '',
    image_url: '',
    ethnicity: '',
    gender: '',
    is_active: true,
    display_order: 0
  });

  // Events state
  const [events, setEvents] = useState([]);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const emptyEventForm = {
    title: '', description: '', date: '', time_start: '', time_end: '', location: '',
    event_type: 'registration', image_url: '', registration_link: ''
  };
  const [eventForm, setEventForm] = useState(emptyEventForm);
  const [eventError, setEventError] = useState('');

  // Partners state
  const [partners, setPartners] = useState([]);
  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const emptyPartnerForm = { name: '', logo_url: '', website: '', is_active: true, display_order: 0 };
  const [partnerForm, setPartnerForm] = useState(emptyPartnerForm);

  // Impact stats settings state
  const [impactForm, setImpactForm] = useState({
    athletes_supported: '', programs_funded: '', scholarships_awarded: '', communities_reached: ''
  });
  const [impactSaved, setImpactSaved] = useState(false);

  // Restore an existing session on mount; an expired token is rejected by the
  // API and the response interceptor sends us back to the login screen.
  useEffect(() => {
    handleUnauthorized = () => {
      setIsLoggedIn(false);
      setSessionExpired(true);
    };
    if (adminToken) {
      setIsLoggedIn(true);
      fetchAllData();
    }
    return () => {
      handleUnauthorized = () => {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError('');
    setSessionExpired(false);

    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      if (!res.data?.token) {
        setLoginError('Login failed - no session token returned');
        return;
      }
      setAdminToken(res.data.token);
      setIsLoggedIn(true);
      setPassword('');
      fetchAllData();
    } catch (error) {
      setLoginError('Invalid credentials');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    setAdminToken('');
    setIsLoggedIn(false);
    setUsername('');
    setPassword('');
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, donationsRes, contactsRes, volunteersRes, sponsorsRes, newsletterRes, athletesRes, eventsRes, partnersRes, impactRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`),
        axios.get(`${API}/admin/donations`),
        axios.get(`${API}/contact`),
        axios.get(`${API}/volunteers`),
        axios.get(`${API}/sponsors/inquiry`),
        axios.get(`${API}/admin/newsletter`),
        axios.get(`${API}/admin/athletes`),
        axios.get(`${API}/admin/events`),
        axios.get(`${API}/admin/partners`),
        axios.get(`${API}/impact/stats`)
      ]);

      setDashboardStats(statsRes.data);
      setDonations(donationsRes.data);
      setContacts(contactsRes.data);
      setVolunteers(volunteersRes.data);
      setSponsors(sponsorsRes.data);
      setNewsletter(newsletterRes.data);
      setAthletes(athletesRes.data);
      setEvents(eventsRes.data);
      setPartners(partnersRes.data);
      setImpactForm({
        athletes_supported: impactRes.data.athletes_supported ?? '',
        programs_funded: impactRes.data.programs_funded ?? '',
        scholarships_awarded: impactRes.data.scholarships_awarded ?? '',
        communities_reached: impactRes.data.communities_reached ?? ''
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const seedAthletes = async () => {
    try {
      await axios.post(`${API}/admin/athletes/seed`);
      fetchAllData();
    } catch (error) {
      console.error('Error seeding athletes:', error);
    }
  };

  const updateStatus = async (type, id, status) => {
    try {
      await axios.put(`${API}/admin/${type}/${id}/status?status=${status}`);
      fetchAllData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleAthleteSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...athleteForm,
        age: parseInt(athleteForm.age),
        display_order: parseInt(athleteForm.display_order) || 0
      };
      
      if (editingAthlete) {
        await axios.put(`${API}/admin/athletes/${editingAthlete.id}`, data);
      } else {
        await axios.post(`${API}/admin/athletes`, data);
      }
      
      setIsAthleteModalOpen(false);
      setEditingAthlete(null);
      setAthleteForm({
        name: '', age: '', sport: '', achievement: '', story: '',
        image_url: '', ethnicity: '', gender: '', is_active: true, display_order: 0
      });
      fetchAllData();
    } catch (error) {
      console.error('Error saving athlete:', error);
    }
  };

  const deleteAthlete = async (id) => {
    if (window.confirm('Are you sure you want to delete this athlete?')) {
      try {
        await axios.delete(`${API}/admin/athletes/${id}`);
        fetchAllData();
      } catch (error) {
        console.error('Error deleting athlete:', error);
      }
    }
  };

  const openEditAthlete = (athlete) => {
    setEditingAthlete(athlete);
    setAthleteForm({
      name: athlete.name,
      age: athlete.age.toString(),
      sport: athlete.sport,
      achievement: athlete.achievement || '',
      story: athlete.story || '',
      image_url: athlete.image_url,
      ethnicity: athlete.ethnicity,
      gender: athlete.gender,
      is_active: athlete.is_active,
      display_order: athlete.display_order
    });
    setIsAthleteModalOpen(true);
  };

  // ---- Events ----
  const handleEventSubmit = async (e) => {
    e.preventDefault();
    setEventError('');

    const { time_start, time_end, ...rest } = eventForm;
    if (!time_start || !time_end) {
      setEventError('Please choose a start and end time.');
      return;
    }

    const data = { ...rest, time: `${time_start} - ${time_end}` };

    try {
      if (editingEvent) {
        await axios.put(`${API}/admin/events/${editingEvent.id}`, data);
      } else {
        await axios.post(`${API}/admin/events`, data);
      }
      setIsEventModalOpen(false);
      setEditingEvent(null);
      setEventForm(emptyEventForm);
      fetchAllData();
    } catch (error) {
      console.error('Error saving event:', error);
      setEventError('Could not save the event. Please try again.');
    }
  };

  const deleteEvent = async (id) => {
    if (window.confirm('Delete this event?')) {
      try {
        await axios.delete(`${API}/admin/events/${id}`);
        fetchAllData();
      } catch (error) {
        console.error('Error deleting event:', error);
      }
    }
  };

  const openEditEvent = (event) => {
    const { start, end } = parseTimeRange(event.time);
    setEditingEvent(event);
    setEventError('');
    setEventForm({
      title: event.title || '',
      description: event.description || '',
      date: event.date || '',
      time_start: start,
      time_end: end,
      location: event.location || '',
      event_type: event.event_type || 'registration',
      image_url: event.image_url || '',
      registration_link: event.registration_link || ''
    });
    setIsEventModalOpen(true);
  };

  // ---- Partners ----
  const handlePartnerSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...partnerForm, display_order: parseInt(partnerForm.display_order) || 0 };
      if (editingPartner) {
        await axios.put(`${API}/admin/partners/${editingPartner.id}`, data);
      } else {
        await axios.post(`${API}/admin/partners`, data);
      }
      setIsPartnerModalOpen(false);
      setEditingPartner(null);
      setPartnerForm(emptyPartnerForm);
      fetchAllData();
    } catch (error) {
      console.error('Error saving partner:', error);
    }
  };

  const deletePartner = async (id) => {
    if (window.confirm('Delete this partner?')) {
      try {
        await axios.delete(`${API}/admin/partners/${id}`);
        fetchAllData();
      } catch (error) {
        console.error('Error deleting partner:', error);
      }
    }
  };

  const openEditPartner = (partner) => {
    setEditingPartner(partner);
    setPartnerForm({
      name: partner.name || '',
      logo_url: partner.logo_url || '',
      website: partner.website || '',
      is_active: partner.is_active,
      display_order: partner.display_order
    });
    setIsPartnerModalOpen(true);
  };

  // ---- Impact stats ----
  const handleImpactSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {};
      ['athletes_supported', 'programs_funded', 'scholarships_awarded', 'communities_reached'].forEach((k) => {
        if (impactForm[k] !== '' && impactForm[k] !== null) data[k] = parseInt(impactForm[k]);
      });
      await axios.put(`${API}/admin/impact/stats`, data);
      setImpactSaved(true);
      setTimeout(() => setImpactSaved(false), 3000);
      fetchAllData();
    } catch (error) {
      console.error('Error saving impact stats:', error);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) return;
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(h => JSON.stringify(row[h] || '')).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4" data-testid="admin-login">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 w-full max-w-md"
        >
          <div className="text-center mb-8">
            <Lock className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-slate-600 mt-2">Lane 4 For All Kids Foundation</p>
          </div>
          
          <form onSubmit={handleLogin}>
            <div className="space-y-4">
              <div>
                <Label>Username</Label>
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="mt-1"
                  data-testid="admin-username"
                  required
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1"
                  data-testid="admin-password"
                  required
                />
              </div>
              
              {sessionExpired && !loginError && (
                <p className="text-amber-600 text-sm" data-testid="session-expired">
                  Your session expired. Please log in again.
                </p>
              )}

              {loginError && (
                <p className="text-red-600 text-sm">{loginError}</p>
              )}
              
              <Button
                type="submit"
                disabled={isLoggingIn}
                className="w-full bg-red-600 text-white hover:bg-slate-900"
                data-testid="admin-login-btn"
              >
                {isLoggingIn ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Logging in...</>
                ) : (
                  'Login'
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-slate-900 text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold">Admin Dashboard</h1>
            <p className="text-slate-400 text-sm">Lane 4 For All Kids Foundation</p>
          </div>
          <div className="flex items-center space-x-4">
            <Button
              onClick={fetchAllData}
              variant="outline"
              className="border-slate-600 text-white hover:bg-slate-800"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-red-600 text-red-400 hover:bg-red-900"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-red-600 animate-spin mx-auto" />
            <p className="mt-4 text-slate-600">Loading dashboard...</p>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-8">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="donations" data-testid="tab-donations">Donations</TabsTrigger>
              <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
              <TabsTrigger value="volunteers" data-testid="tab-volunteers">Volunteers</TabsTrigger>
              <TabsTrigger value="sponsors" data-testid="tab-sponsors">Sponsors</TabsTrigger>
              <TabsTrigger value="newsletter" data-testid="tab-newsletter">Newsletter</TabsTrigger>
              <TabsTrigger value="athletes" data-testid="tab-athletes">Athletes</TabsTrigger>
              <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
              <TabsTrigger value="partners" data-testid="tab-partners">Partners</TabsTrigger>
              <TabsTrigger value="sitestats" data-testid="tab-sitestats">Site Stats</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                <StatCard
                  icon={DollarSign}
                  label="Total Raised"
                  value={`$${dashboardStats?.donations?.total_raised?.toLocaleString() || 0}`}
                  color="bg-green-100 text-green-600"
                />
                <StatCard
                  icon={DollarSign}
                  label="Donations"
                  value={dashboardStats?.donations?.paid || 0}
                  color="bg-blue-100 text-blue-600"
                />
                <StatCard
                  icon={Mail}
                  label="Contacts"
                  value={dashboardStats?.contacts?.total || 0}
                  badge={dashboardStats?.contacts?.new}
                  color="bg-purple-100 text-purple-600"
                />
                <StatCard
                  icon={Users}
                  label="Volunteers"
                  value={dashboardStats?.volunteers?.total || 0}
                  badge={dashboardStats?.volunteers?.new}
                  color="bg-orange-100 text-orange-600"
                />
                <StatCard
                  icon={Building2}
                  label="Sponsors"
                  value={dashboardStats?.sponsors?.total || 0}
                  badge={dashboardStats?.sponsors?.new}
                  color="bg-amber-100 text-amber-600"
                />
                <StatCard
                  icon={Trophy}
                  label="Athletes"
                  value={dashboardStats?.athletes?.total || 0}
                  color="bg-red-100 text-red-600"
                />
              </div>

              {/* Quick Actions */}
              <div className="bg-white p-6 border border-slate-200">
                <h3 className="font-heading text-lg font-bold mb-4">Quick Actions</h3>
                <div className="flex flex-wrap gap-4">
                  <Button onClick={() => setActiveTab('donations')} variant="outline">
                    View Donations
                  </Button>
                  <Button onClick={() => setActiveTab('contacts')} variant="outline">
                    View Contacts ({dashboardStats?.contacts?.new || 0} new)
                  </Button>
                  <Button onClick={() => setActiveTab('athletes')} variant="outline">
                    Manage Athletes
                  </Button>
                  <Button onClick={() => exportToCSV(newsletter, 'newsletter_subscribers')} variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    Export Newsletter List
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Donations Tab */}
            <TabsContent value="donations">
              <div className="bg-white border border-slate-200">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="font-heading text-lg font-bold">All Donations</h3>
                  <Button onClick={() => exportToCSV(donations, 'donations')} variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Donor</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Recurring</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {donations.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-slate-500">No donations yet</td>
                        </tr>
                      ) : donations.map((d) => (
                        <tr key={d.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm">{formatDate(d.created_at)}</td>
                          <td className="px-4 py-3 text-sm font-medium">{d.donor_name || 'Anonymous'}</td>
                          <td className="px-4 py-3 text-sm">{d.donor_email || '-'}</td>
                          <td className="px-4 py-3 text-sm font-bold text-green-600">${d.amount?.toFixed(2)}</td>
                          <td className="px-4 py-3">
                            <StatusBadge status={d.payment_status} />
                          </td>
                          <td className="px-4 py-3 text-sm">{d.is_recurring ? 'Yes' : 'No'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Contacts Tab */}
            <TabsContent value="contacts">
              <DataTable
                title="Contact Submissions"
                data={contacts}
                columns={['created_at', 'name', 'email', 'subject', 'status']}
                onExport={() => exportToCSV(contacts, 'contacts')}
                onStatusUpdate={(id, status) => updateStatus('contact', id, status)}
                renderRow={(c) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm">{formatDate(c.created_at)}</td>
                    <td className="px-4 py-3 text-sm font-medium">{c.name}</td>
                    <td className="px-4 py-3 text-sm">{c.email}</td>
                    <td className="px-4 py-3 text-sm">{c.subject}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={c.status} />
                    </td>
                    <td className="px-4 py-3">
                      <Select
                        value={c.status}
                        onValueChange={(val) => updateStatus('contact', c.id, val)}
                      >
                        <SelectTrigger className="w-32 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                )}
              />
            </TabsContent>

            {/* Volunteers Tab */}
            <TabsContent value="volunteers">
              <DataTable
                title="Volunteer Signups"
                data={volunteers}
                columns={['created_at', 'name', 'email', 'interests', 'status']}
                onExport={() => exportToCSV(volunteers, 'volunteers')}
                renderRow={(v) => (
                  <tr key={v.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm">{formatDate(v.created_at)}</td>
                    <td className="px-4 py-3 text-sm font-medium">{v.name}</td>
                    <td className="px-4 py-3 text-sm">{v.email}</td>
                    <td className="px-4 py-3 text-sm">{v.interests?.join(', ') || '-'}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={v.status} />
                    </td>
                    <td className="px-4 py-3">
                      <Select
                        value={v.status}
                        onValueChange={(val) => updateStatus('volunteers', v.id, val)}
                      >
                        <SelectTrigger className="w-32 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                )}
              />
            </TabsContent>

            {/* Sponsors Tab */}
            <TabsContent value="sponsors">
              <DataTable
                title="Sponsor Inquiries"
                data={sponsors}
                columns={['created_at', 'company_name', 'contact_name', 'tier', 'status']}
                onExport={() => exportToCSV(sponsors, 'sponsors')}
                renderRow={(s) => (
                  <tr key={s.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm">{formatDate(s.created_at)}</td>
                    <td className="px-4 py-3 text-sm font-medium">{s.company_name}</td>
                    <td className="px-4 py-3 text-sm">{s.contact_name}</td>
                    <td className="px-4 py-3 text-sm capitalize">{s.sponsorship_tier}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={s.status} />
                    </td>
                    <td className="px-4 py-3">
                      <Select
                        value={s.status}
                        onValueChange={(val) => updateStatus('sponsors', s.id, val)}
                      >
                        <SelectTrigger className="w-32 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="negotiating">Negotiating</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                )}
              />
            </TabsContent>

            {/* Newsletter Tab */}
            <TabsContent value="newsletter">
              <div className="bg-white border border-slate-200">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="font-heading text-lg font-bold">Newsletter Subscribers ({newsletter.length})</h3>
                  <Button onClick={() => exportToCSV(newsletter, 'newsletter')} variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Email</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {newsletter.length === 0 ? (
                        <tr>
                          <td colSpan={2} className="px-4 py-8 text-center text-slate-500">No subscribers yet</td>
                        </tr>
                      ) : newsletter.map((n) => (
                        <tr key={n.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm">{formatDate(n.created_at)}</td>
                          <td className="px-4 py-3 text-sm">{n.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Athletes Tab */}
            <TabsContent value="athletes">
              <div className="bg-white border border-slate-200">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="font-heading text-lg font-bold">Athlete Profiles ({athletes.length})</h3>
                  <div className="flex items-center space-x-2">
                    {athletes.length === 0 && (
                      <Button onClick={seedAthletes} variant="outline" size="sm" data-testid="seed-athletes-btn">
                        Seed Sample Athletes
                      </Button>
                    )}
                    <Button
                      onClick={() => {
                        setEditingAthlete(null);
                        setAthleteForm({
                          name: '', age: '', sport: '', achievement: '', story: '',
                          image_url: '', ethnicity: '', gender: '', is_active: true, display_order: 0
                        });
                        setIsAthleteModalOpen(true);
                      }}
                      className="bg-red-600 text-white hover:bg-red-700"
                      size="sm"
                      data-testid="add-athlete-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Athlete
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4">
                  {athletes.length === 0 ? (
                    <div className="col-span-full text-center py-8 text-slate-500">
                      No athletes yet. Click "Seed Sample Athletes" to add initial profiles.
                    </div>
                  ) : athletes.map((athlete) => (
                    <div
                      key={athlete.id}
                      className={`border ${athlete.is_active ? 'border-slate-200' : 'border-red-200 bg-red-50'} p-4`}
                      data-testid={`athlete-card-${athlete.id}`}
                    >
                      <img
                        src={athlete.image_url}
                        alt={athlete.name}
                        className="w-full h-40 object-cover mb-3"
                      />
                      <h4 className="font-bold text-slate-900">{athlete.name}</h4>
                      <p className="text-sm text-slate-600">Age {athlete.age} • {athlete.sport}</p>
                      <p className="text-xs text-red-600 font-medium mt-1">{athlete.achievement}</p>
                      <div className="flex items-center justify-between mt-3">
                        <span className={`text-xs px-2 py-1 ${athlete.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {athlete.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openEditAthlete(athlete)}
                            className="text-slate-400 hover:text-blue-600"
                            data-testid={`edit-athlete-${athlete.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteAthlete(athlete.id)}
                            className="text-slate-400 hover:text-red-600"
                            data-testid={`delete-athlete-${athlete.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Athlete Modal */}
              <Dialog open={isAthleteModalOpen} onOpenChange={setIsAthleteModalOpen}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>{editingAthlete ? 'Edit Athlete' : 'Add New Athlete'}</DialogTitle>
                    <DialogDescription>
                      {editingAthlete ? 'Update athlete profile information.' : 'Add a new athlete to showcase on the Impact page.'}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAthleteSubmit}>
                    <div className="grid grid-cols-2 gap-4 py-4">
                      <div>
                        <Label>Name *</Label>
                        <Input
                          required
                          value={athleteForm.name}
                          onChange={(e) => setAthleteForm(prev => ({ ...prev, name: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Age *</Label>
                        <Input
                          type="number"
                          required
                          min="5"
                          max="18"
                          value={athleteForm.age}
                          onChange={(e) => setAthleteForm(prev => ({ ...prev, age: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Sport *</Label>
                        <Input
                          required
                          value={athleteForm.sport}
                          onChange={(e) => setAthleteForm(prev => ({ ...prev, sport: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Achievement</Label>
                        <Input
                          value={athleteForm.achievement}
                          onChange={(e) => setAthleteForm(prev => ({ ...prev, achievement: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Ethnicity *</Label>
                        <Select
                          value={athleteForm.ethnicity}
                          onValueChange={(val) => setAthleteForm(prev => ({ ...prev, ethnicity: val }))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select ethnicity" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="african_american">African American</SelectItem>
                            <SelectItem value="white">White American</SelectItem>
                            <SelectItem value="latino">Latino/Hispanic</SelectItem>
                            <SelectItem value="asian">Asian</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Gender *</Label>
                        <Select
                          value={athleteForm.gender}
                          onValueChange={(val) => setAthleteForm(prev => ({ ...prev, gender: val }))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label>Image URL *</Label>
                        <Input
                          required
                          value={athleteForm.image_url}
                          onChange={(e) => setAthleteForm(prev => ({ ...prev, image_url: e.target.value }))}
                          className="mt-1"
                          placeholder="https://..."
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Story</Label>
                        <Textarea
                          value={athleteForm.story}
                          onChange={(e) => setAthleteForm(prev => ({ ...prev, story: e.target.value }))}
                          className="mt-1"
                          rows={3}
                        />
                      </div>
                      <div>
                        <Label>Display Order</Label>
                        <Input
                          type="number"
                          value={athleteForm.display_order}
                          onChange={(e) => setAthleteForm(prev => ({ ...prev, display_order: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div className="flex items-center space-x-2 mt-6">
                        <input
                          type="checkbox"
                          checked={athleteForm.is_active}
                          onChange={(e) => setAthleteForm(prev => ({ ...prev, is_active: e.target.checked }))}
                          id="is_active"
                        />
                        <Label htmlFor="is_active">Active (visible on website)</Label>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setIsAthleteModalOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-red-600 text-white hover:bg-red-700">
                        {editingAthlete ? 'Update Athlete' : 'Add Athlete'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </TabsContent>

            {/* Events Tab */}
            <TabsContent value="events">
              <div className="bg-white border border-slate-200">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="font-heading text-lg font-bold">Events ({events.length})</h3>
                  <Button
                    onClick={() => { setEditingEvent(null); setEventForm(emptyEventForm); setEventError(''); setIsEventModalOpen(true); }}
                    className="bg-red-600 text-white hover:bg-red-700"
                    size="sm"
                    data-testid="add-event-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Event
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Title</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {events.length === 0 ? (
                        <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No events yet. The site shows sample events until you add real ones.</td></tr>
                      ) : events.map((ev) => (
                        <tr key={ev.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium">{ev.title}</td>
                          <td className="px-4 py-3 text-sm capitalize">{ev.event_type}</td>
                          <td className="px-4 py-3 text-sm">{ev.date}</td>
                          <td className="px-4 py-3 text-sm">{ev.location}</td>
                          <td className="px-4 py-3">
                            <div className="flex space-x-2">
                              <button onClick={() => openEditEvent(ev)} className="text-slate-400 hover:text-blue-600"><Edit className="w-4 h-4" /></button>
                              <button onClick={() => deleteEvent(ev.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <Dialog open={isEventModalOpen} onOpenChange={setIsEventModalOpen}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>{editingEvent ? 'Edit Event' : 'Add New Event'}</DialogTitle>
                    <DialogDescription>Events appear on the public Events page.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleEventSubmit}>
                    <div className="grid grid-cols-2 gap-4 py-4">
                      <div className="col-span-2">
                        <Label>Title *</Label>
                        <Input required value={eventForm.title} onChange={(e) => setEventForm(p => ({ ...p, title: e.target.value }))} className="mt-1" />
                      </div>
                      <div>
                        <Label>Type *</Label>
                        <Select value={eventForm.event_type} onValueChange={(val) => setEventForm(p => ({ ...p, event_type: val }))}>
                          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="registration">Registration</SelectItem>
                            <SelectItem value="fundraiser">Fundraiser</SelectItem>
                            <SelectItem value="volunteer">Volunteer</SelectItem>
                            <SelectItem value="competition">Competition</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Date * (e.g. March 15, 2026)</Label>
                        <Input required value={eventForm.date} onChange={(e) => setEventForm(p => ({ ...p, date: e.target.value }))} className="mt-1" />
                      </div>
                      <div>
                        <Label>Start Time *</Label>
                        <Select
                          value={eventForm.time_start}
                          onValueChange={(val) => setEventForm(p => ({ ...p, time_start: val }))}
                        >
                          <SelectTrigger className="mt-1" data-testid="event-time-start">
                            <SelectValue placeholder="Select start time" />
                          </SelectTrigger>
                          <SelectContent className="max-h-64">
                            {timeChoices(eventForm.time_start).map((t) => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>End Time *</Label>
                        <Select
                          value={eventForm.time_end}
                          onValueChange={(val) => setEventForm(p => ({ ...p, time_end: val }))}
                        >
                          <SelectTrigger className="mt-1" data-testid="event-time-end">
                            <SelectValue placeholder="Select end time" />
                          </SelectTrigger>
                          <SelectContent className="max-h-64">
                            {timeChoices(eventForm.time_end).map((t) => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label>Location *</Label>
                        <Input required value={eventForm.location} onChange={(e) => setEventForm(p => ({ ...p, location: e.target.value }))} className="mt-1" />
                      </div>
                      <div className="col-span-2">
                        <Label>Description *</Label>
                        <Textarea required rows={3} value={eventForm.description} onChange={(e) => setEventForm(p => ({ ...p, description: e.target.value }))} className="mt-1" />
                      </div>
                      <div className="col-span-2">
                        <Label>Registration Link (optional)</Label>
                        <Input value={eventForm.registration_link} onChange={(e) => setEventForm(p => ({ ...p, registration_link: e.target.value }))} className="mt-1" placeholder="https://..." />
                      </div>
                    </div>
                    {eventError && (
                      <p className="text-red-600 text-sm mb-2" data-testid="event-error">{eventError}</p>
                    )}
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setIsEventModalOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-red-600 text-white hover:bg-red-700">{editingEvent ? 'Update Event' : 'Add Event'}</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </TabsContent>

            {/* Partners Tab */}
            <TabsContent value="partners">
              <div className="bg-white border border-slate-200">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="font-heading text-lg font-bold">Partners / Sponsor Logos ({partners.length})</h3>
                  <Button
                    onClick={() => { setEditingPartner(null); setPartnerForm(emptyPartnerForm); setIsPartnerModalOpen(true); }}
                    className="bg-red-600 text-white hover:bg-red-700"
                    size="sm"
                    data-testid="add-partner-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Partner
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4">
                  {partners.length === 0 ? (
                    <div className="col-span-full text-center py-8 text-slate-500">
                      No partners yet. Add one to show its logo in "Current Sponsors" on the Sponsors page.
                    </div>
                  ) : partners.map((p) => (
                    <div key={p.id} className={`border ${p.is_active ? 'border-slate-200' : 'border-red-200 bg-red-50'} p-4`}>
                      <div className="h-24 flex items-center justify-center bg-white border border-slate-100 mb-3">
                        <img src={p.logo_url} alt={p.name} className="max-h-20 max-w-full object-contain" />
                      </div>
                      <h4 className="font-bold text-slate-900 text-sm">{p.name}</h4>
                      <div className="flex items-center justify-between mt-2">
                        <span className={`text-xs px-2 py-1 ${p.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {p.is_active ? 'Active' : 'Hidden'}
                        </span>
                        <div className="flex space-x-2">
                          <button onClick={() => openEditPartner(p)} className="text-slate-400 hover:text-blue-600"><Edit className="w-4 h-4" /></button>
                          <button onClick={() => deletePartner(p.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Dialog open={isPartnerModalOpen} onOpenChange={setIsPartnerModalOpen}>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>{editingPartner ? 'Edit Partner' : 'Add New Partner'}</DialogTitle>
                    <DialogDescription>Logos show in the "Current Sponsors" section on the Sponsors page.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handlePartnerSubmit}>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>Partner Name *</Label>
                        <Input required value={partnerForm.name} onChange={(e) => setPartnerForm(p => ({ ...p, name: e.target.value }))} className="mt-1" />
                      </div>
                      <div>
                        <Label>Logo Image URL *</Label>
                        <Input required value={partnerForm.logo_url} onChange={(e) => setPartnerForm(p => ({ ...p, logo_url: e.target.value }))} className="mt-1" placeholder="https://..." />
                      </div>
                      <div>
                        <Label>Website (optional)</Label>
                        <Input value={partnerForm.website} onChange={(e) => setPartnerForm(p => ({ ...p, website: e.target.value }))} className="mt-1" placeholder="https://..." />
                      </div>
                      <div>
                        <Label>Display Order</Label>
                        <Input type="number" value={partnerForm.display_order} onChange={(e) => setPartnerForm(p => ({ ...p, display_order: e.target.value }))} className="mt-1" />
                      </div>
                      <div className="flex items-center space-x-2">
                        <input type="checkbox" checked={partnerForm.is_active} onChange={(e) => setPartnerForm(p => ({ ...p, is_active: e.target.checked }))} id="partner_active" />
                        <Label htmlFor="partner_active">Active (visible on website)</Label>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setIsPartnerModalOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-red-600 text-white hover:bg-red-700">{editingPartner ? 'Update Partner' : 'Add Partner'}</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </TabsContent>

            {/* Site Stats Tab */}
            <TabsContent value="sitestats">
              <div className="bg-white border border-slate-200 p-6 max-w-2xl">
                <h3 className="font-heading text-lg font-bold mb-2">Impact Numbers</h3>
                <p className="text-sm text-slate-600 mb-6">
                  These numbers show on the Impact and Home pages. "Total Funds Raised" is calculated automatically from real donations and isn't edited here.
                </p>
                <form onSubmit={handleImpactSubmit}>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Athletes Supported</Label>
                      <Input type="number" value={impactForm.athletes_supported} onChange={(e) => setImpactForm(p => ({ ...p, athletes_supported: e.target.value }))} className="mt-1" />
                    </div>
                    <div>
                      <Label>Programs Funded</Label>
                      <Input type="number" value={impactForm.programs_funded} onChange={(e) => setImpactForm(p => ({ ...p, programs_funded: e.target.value }))} className="mt-1" />
                    </div>
                    <div>
                      <Label>Scholarships Awarded</Label>
                      <Input type="number" value={impactForm.scholarships_awarded} onChange={(e) => setImpactForm(p => ({ ...p, scholarships_awarded: e.target.value }))} className="mt-1" />
                    </div>
                    <div>
                      <Label>Communities Reached</Label>
                      <Input type="number" value={impactForm.communities_reached} onChange={(e) => setImpactForm(p => ({ ...p, communities_reached: e.target.value }))} className="mt-1" />
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 mt-6">
                    <Button type="submit" className="bg-red-600 text-white hover:bg-red-700">Save Numbers</Button>
                    {impactSaved && <span className="text-green-600 text-sm flex items-center"><Check className="w-4 h-4 mr-1" /> Saved</span>}
                  </div>
                </form>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

// Helper Components
const StatCard = ({ icon: Icon, label, value, badge, color }) => (
  <div className="bg-white p-4 border border-slate-200">
    <div className={`w-10 h-10 ${color} flex items-center justify-center mb-3`}>
      <Icon className="w-5 h-5" />
    </div>
    <p className="text-2xl font-bold text-slate-900">{value}</p>
    <p className="text-sm text-slate-600">{label}</p>
    {badge > 0 && (
      <span className="inline-block mt-2 bg-red-100 text-red-700 text-xs px-2 py-1">
        {badge} new
      </span>
    )}
  </div>
);

const StatusBadge = ({ status }) => {
  const colors = {
    new: 'bg-blue-100 text-blue-700',
    pending: 'bg-yellow-100 text-yellow-700',
    paid: 'bg-green-100 text-green-700',
    in_progress: 'bg-purple-100 text-purple-700',
    resolved: 'bg-green-100 text-green-700',
    contacted: 'bg-blue-100 text-blue-700',
    active: 'bg-green-100 text-green-700',
    negotiating: 'bg-orange-100 text-orange-700',
    confirmed: 'bg-green-100 text-green-700',
    expired: 'bg-red-100 text-red-700',
    unpaid: 'bg-red-100 text-red-700'
  };
  
  return (
    <span className={`inline-block px-2 py-1 text-xs font-medium capitalize ${colors[status] || 'bg-slate-100 text-slate-700'}`}>
      {status?.replace('_', ' ')}
    </span>
  );
};

const DataTable = ({ title, data, columns, onExport, renderRow }) => (
  <div className="bg-white border border-slate-200">
    <div className="p-4 border-b border-slate-200 flex items-center justify-between">
      <h3 className="font-heading text-lg font-bold">{title} ({data.length})</h3>
      <Button onClick={onExport} variant="outline" size="sm">
        <Download className="w-4 h-4 mr-2" />
        Export CSV
      </Button>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-slate-50">
          <tr>
            {columns.map(col => (
              <th key={col} className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                {col.replace('_', ' ')}
              </th>
            ))}
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 1} className="px-4 py-8 text-center text-slate-500">No data yet</td>
            </tr>
          ) : data.map(renderRow)}
        </tbody>
      </table>
    </div>
  </div>
);

export default Admin;
